import { forkJoin } from "rxjs";
import { ajax } from "rxjs/ajax";
import { mergeMap, delay, map, catchError, takeUntil } from "rxjs/operators";

import { equals, pickBy } from "ramda";
import { combineEpics, ofType } from "redux-observable";
import { API_BASE_URL, CATEGORY_BASE_URL } from "props";
import { apiAjax, errorToAction } from "util/ajaxUtil";
import * as applicationActionTypes from "application/redux/applicationActionTypes";
import { mediaItemUpdated } from "application/redux/applicationActions";
import {
  actionTypes,
  fetchVocabularyComplete,
  saveMediaItemComplete,
  thumbnailUpdateComplete,
} from "edit-media/redux/editMediaActions";
import {
  getEditMediaItem,
  getEditMediaOriginalMediaItem,
} from "common/selectors";
import {
  hasPurchaseLink,
  getFormattedPurchaseLink,
} from "../../util/mediaUtil";

const BISAC_URL = `${CATEGORY_BASE_URL}/bisacs`;
const CONTRIBUTOR_ROLES_URL = `${API_BASE_URL}/vocabularies/contributor-roles`;
const CONTRIBUTOR_TYPES_URL = `${API_BASE_URL}/vocabularies/contributor-types`;
const LANGUAGES_URL = `${API_BASE_URL}/vocabularies/languages`;
const LICENSE_TYPES_URL = `${API_BASE_URL}/vocabularies/license-types`;

const getPatchMediaUrl = mediaId => `${API_BASE_URL}/admin/content/${mediaId}`;
const getContentThumbnailUpdateUrl = mediaId =>
  `${API_BASE_URL}/admin/content/${mediaId}/thumbnail`;

const objectDiff = media => (val, key) => !equals(media[key], val);

const ajaxGetBisacs = state =>
  apiAjax(state)({
    url: BISAC_URL,
  }).pipe(map(ajaxResponse => ajaxResponse.response));

const ajaxGetContributorRoles = state =>
  apiAjax(state)({
    url: CONTRIBUTOR_ROLES_URL,
  }).pipe(map(ajaxResponse => ajaxResponse.response));

const ajaxGetContributorTypes = state =>
  apiAjax(state)({
    url: CONTRIBUTOR_TYPES_URL,
  }).pipe(map(ajaxResponse => ajaxResponse.response));

const ajaxGetLanguages = state =>
  apiAjax(state)({
    url: LANGUAGES_URL,
  }).pipe(map(ajaxResponse => ajaxResponse.response));

const ajaxGetLicenseTypes = state =>
  apiAjax(state)({
    url: LICENSE_TYPES_URL,
  }).pipe(map(ajaxResponse => ajaxResponse.response));

const ajaxPatchMedia = state => (mediaId, update) =>
  apiAjax(state)({
    url: getPatchMediaUrl(mediaId),
    method: "PATCH",
    body: update,
  });

const ajaxUpdateContentThumbnail = state => (mediaId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  return ajax({
    url: getContentThumbnailUpdateUrl(mediaId),
    method: "PUT",
    crossDomain: true,
    headers: {
      Authorization: `Bearer ${state.auth.user.token}`,
    },
    body: formData,
  }).pipe(map(ajaxResponse => ajaxResponse.xhr.getResponseHeader("Location")));
};

const fetchEditMediaLookupDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.FETCH_VOCABULARY),
    mergeMap(() =>
      forkJoin([
        ajaxGetLanguages(state$.value),
        ajaxGetLicenseTypes(state$.value),
        ajaxGetBisacs(state$.value),
        ajaxGetContributorRoles(state$.value),
        ajaxGetContributorTypes(state$.value),
      ]).pipe(
        map(results => fetchVocabularyComplete(...results)),
        catchError(errorToAction),
        takeUntil(action$.pipe(ofType(applicationActionTypes.ROUTE_CHANGED))),
      ),
    ),
  );

const saveMediaItemEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.SAVE_MEDIA_ITEM),
    mergeMap(() => {
      const state = state$.value;
      const originalMediaItem = getEditMediaOriginalMediaItem(state);
      const mediaUpdateItem = getEditMediaItem(state);

      const update = pickBy(objectDiff(originalMediaItem), mediaUpdateItem);
      // PATCH request requires both purchaseLink.title and purchaseLink.url
      if (hasPurchaseLink(update)) {
        update.purchaseLink = getFormattedPurchaseLink(update);
      }

      return ajaxPatchMedia(state$.value)(originalMediaItem.id, update).pipe(
        map(() => saveMediaItemComplete(mediaUpdateItem)),
        catchError(errorToAction),
      );
    }),
  );

const saveMediaItemCompleteEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.SAVE_MEDIA_ITEM_COMPLETE),
    map(action => mediaItemUpdated(action.mediaItem)),
  );

const saveThumbnailFileEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.SAVE_THUMBNAIL_FILE),
    mergeMap(action =>
      ajaxUpdateContentThumbnail(state$.value)(
        action.mediaId,
        action.file,
      ).pipe(
        delay(3000), // Delay to get time for the image to invalidate
        map(thumbnailUrl => thumbnailUpdateComplete(thumbnailUrl)),
      ),
    ),
  );

export default combineEpics(
  fetchEditMediaLookupDataEpic,
  saveMediaItemEpic,
  saveMediaItemCompleteEpic,
  saveThumbnailFileEpic,
);
